import { useState } from 'react'
import { Box, Button, IconButton, Input, Typography } from '@mui/material'
import MessageIcon from '@assets/crm/Message.svg'
import AddIcon from '@assets/crm/Add.svg'
import CommentPersonIcon from '@assets/crm/CommentPerson.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import type { APICrmComment } from '@/services/crmService'
import { formatCrmDateTime } from '@/utils/crm'

const CommentsAreaTaskCreator = ({
  comments,
  value,
  onChange,
  onSubmit,
  submitting = false,
}: {
  comments: APICrmComment[]
  /** The comment being written. */
  value: string
  onChange: (value: string) => void
  /** Edit mode: posts the comment now. Absent in create mode, where it is saved with the task. */
  onSubmit?: () => void
  submitting?: boolean
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const [showInput, setShowInput] = useState(false)

  const BORDER_COLOR = isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'
  const TEXT_COLOR = isDarkMode ? '#FFFFFF' : '#003F61'
  const SUBTEXT_COLOR = isDarkMode ? '#B0B0B0' : '#C0C2D6'
  const BG_COLOR = isDarkMode ? '#091327' : '#FFF'

  return (
    <Box sx={{ paddingRight: '22px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${BORDER_COLOR}`,
          paddingBottom: '12px',
        }}
      >
        <IconButton
          size="small"
          aria-label="הוספת הערה"
          onClick={() => setShowInput(prev => !prev)}
        >
          <img
            src={AddIcon}
            alt="AddIcon"
            style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
          />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            sx={{ fontFamily: 'Rubik, sans-serif', color: TEXT_COLOR }}
          >
            התכתבויות
          </Typography>
          <img
            src={MessageIcon}
            alt="MessageIcon"
            style={{ filter: isDarkMode ? 'brightness(0) invert(1)' : 'none' }}
          />
        </Box>
      </Box>
      {(showInput || value) && (
        <Box sx={{ my: '8px' }}>
          <Box
            sx={{
              borderRadius: '4.5px',
              border: `1px solid ${BORDER_COLOR}`,
              background: BG_COLOR,
              px: '11px',
            }}
          >
            <Input
              dir="rtl"
              multiline
              minRows={2}
              maxRows={6}
              fullWidth
              disableUnderline
              autoFocus
              placeholder="כתיבת הערה"
              value={value}
              disabled={submitting}
              onChange={e => onChange(e.target.value)}
              sx={{
                color: TEXT_COLOR,
                fontFamily: 'Rubik, sans-serif',
                fontSize: '13px',
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: '6px',
            }}
          >
            {onSubmit ? (
              <Button
                variant="text"
                size="small"
                disabled={submitting || !value.trim()}
                onClick={onSubmit}
                sx={{
                  color: '#72A4EB',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                }}
              >
                הוספה
              </Button>
            ) : (
              <span />
            )}
            {!onSubmit && (
              <Typography
                sx={{
                  color: SUBTEXT_COLOR,
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '12px',
                }}
              >
                ההערה תישמר יחד עם המשימה
              </Typography>
            )}
          </Box>
        </Box>
      )}
      <Box>
        {comments.map(comment => (
          <Box
            key={comment.activityId}
            sx={{
              borderBottom: `1px solid ${isDarkMode ? '#374151' : '#E4E7EB'}`,
              paddingBottom: '12px',
              my: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-end',
                marginBottom: '10px',
              }}
            >
              <Typography
                sx={{
                  color: SUBTEXT_COLOR,
                  /* rubik 12 */
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 300,
                  lineHeight: '120%',
                }}
              >
                {formatCrmDateTime(comment.createdAt)}
              </Typography>
              <Typography
                sx={{
                  color: TEXT_COLOR,
                  textAlign: 'center',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '100%',
                }}
              >
                {comment.performedByName || '-'}
              </Typography>
              <img
                src={CommentPersonIcon}
                alt="CommentPersonIcon"
                style={{
                  filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </Box>
            <Box
              sx={{
                borderRadius: '4.5px',
                border: `1px solid ${BORDER_COLOR}`,
                background: BG_COLOR,
                direction: 'rtl',
                padding: '12px 13px',
              }}
            >
              <Typography
                sx={{
                  color: TEXT_COLOR,
                  textAlign: 'right',
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '12.762px',
                  fontStyle: 'normal',
                  fontWeight: 300,
                  lineHeight: '120%',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {comment.comment}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default CommentsAreaTaskCreator
